import { Form, Button, Upload, UploadProps, message, Modal } from "antd";
import { UploadOutlined } from "@ant-design/icons";
import { header } from "../../utils/zippyAdminHeader";
import { apiCall } from "../../../../utils/apiCall";

const toBase64 = (file: any): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
};

const validateFileType = (file: File, validTypes: string[], label: string) => {
  const isValid = validTypes.includes(file.type);
  if (!isValid) message.error(`${file.name} is not a valid ${label} file`);
  return isValid || Upload.LIST_IGNORE;
};

const uploadProps = (
  type: "mp4" | "mp3" | "png",
  form: any,
  name: string,
  storyGroupId: any
): UploadProps => {
  const isMp4Type = type === "mp4";
  const typeMap = {
    mp4: ["video/mp4"],
    mp3: ["audio/mp3", "audio/mpeg"],
    png: ["image/png"],
  };

  return {
    ...(isMp4Type && { maxCount: 1 }),
    multiple: !isMp4Type,
    beforeUpload: async (file) => {
      const currentList = form.getFieldValue(name) || [];

      const existingFile = currentList.find(
        (f: any) => f.name === file.name
      );

      if (existingFile) {
        const shouldContinue = await new Promise<boolean>((resolve) => {
          Modal.confirm({
            title: "The file already exists.",
            content: (
              <>
                "{file.name}" already exists.<br />
                Do you want to continue uploading?
              </>
            ),
            onOk: () => resolve(true),
            onCancel: () => resolve(false),
          });
        });

        if (!shouldContinue) return Upload.LIST_IGNORE; 
      }

      if (isMp4Type) {
        const oldFile = form.getFieldValue(name);

        if (oldFile && oldFile.length > 0) {
          const mediaId = oldFile[0].response?.storyMediaId ?? oldFile[0].uid;

          await apiCall({
            method: "DELETE",
            header: header,
            endPoint:
              process.env.REACT_APP_ZIPPY_ADMIN +
              `/stories/admin/groups/${storyGroupId}/media/${mediaId}`,
          });
        }
      }

      return validateFileType(file, typeMap[type], type.toUpperCase());
    },
  };
};

export default function FormItemUpload({
  storyGroupId,
  userName,
  waitingTimeout,
  setWaitingTimeout,
  uploadQueueRef,
  name,
  btnText,
  type,
  form,
}: {
  storyGroupId: any;
  userName: string | null | undefined;
  waitingTimeout: boolean;
  setWaitingTimeout: any;
  uploadQueueRef: React.MutableRefObject<Promise<any>>;
  name: string;
  btnText: string;
  type: "mp4" | "mp3" | "png";
  form: any,
}) {
  const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));
  const props = uploadProps(type, form, name, storyGroupId);

  const handleCustomRequest = async (options: any) => {
    const { file, onSuccess, onError } = options;

    uploadQueueRef.current = uploadQueueRef.current.then(async () => {
      try {
        await delay(1000);

        const base64 = await toBase64(file);
        const payload = {
          file: {
            fileName: file.name,
            base64File: base64.split(",")[1],
          },
          operator: userName,
        };

        const data = await apiCall({
          method: "POST",
          header: header,
          endPoint:
            process.env.REACT_APP_ZIPPY_ADMIN +
            `/stories/admin/groups/${storyGroupId}/media`,
          payload: payload,
        });
        if (data.storyMediaId) {
          if (data.status === "FILE_OVERWRITTEN") 
            message.success(`${file.name} was overwritten`);
          else
            message.success(`${file.name} uploaded successfully`);

          onSuccess(data, file);
        } else {
          message.error(data.response.data.status || "Upload failed");
          onError(new Error(data.response.data.status || "Upload failed"));
        }
      } catch (e) {
        message.error(`${file.name} upload failed`);
        onError(e);
      }
    });

    uploadQueueRef.current.catch((e) => {
      console.log(e);
    });
  };

  const handleRemove = async (file: any) => {
    let didSetDisabled = false;

    if (file.status === "error") return true;
    else if (file.status !== "done") return false;

    if (waitingTimeout) return false;

    try {
      setWaitingTimeout(true);
      didSetDisabled = true;

      const mediaId = file.response?.storyMediaId ?? file.uid;

      const data = await apiCall({
        method: "DELETE",
        header: header,
        endPoint:
          process.env.REACT_APP_ZIPPY_ADMIN +
          `/stories/admin/groups/${storyGroupId}/media/${mediaId}`,
      });

      if (data && data.status) {
        message.success(`Removed file successfully: ${file.name}`);
        return true;
      } else {
        message.error(`Failed to remove file: ${data.response.data.status}`);
        return false;
      }
    } catch (e) {
      message.error(`Failed to remove file: ${file.name}`);
      return false;
    }
    finally {
      if (didSetDisabled) {
        setTimeout(() => {
          setWaitingTimeout(false);
        }, 1000);
      }
    }
  };

  return (
    <Form.Item
      name={name}
      valuePropName="fileList"
      getValueFromEvent={(e) => e.fileList}
      rules={[{ required: true, message: name + " field is required!" }]}
    >
      <Upload
        {...props}
        customRequest={handleCustomRequest}
        onRemove={handleRemove}
        className="upload-scroll-list"
        onChange={({ fileList }) => {
          const deduped = fileList.reduce((acc: any[], file: any) => {
            const existsIndex = acc.findIndex(f => f.name === file.name);

            if (file.response?.url && !file.url) {
              file.url = file.response.url;
            }

            if (existsIndex > -1) acc[existsIndex] = file;
            else acc.push(file);

            return acc;
          }, []);

          form.setFieldsValue({ [name]: deduped });
        }}
      >
        <Button icon={<UploadOutlined />}>{btnText}</Button>
      </Upload>
    </Form.Item>
  );
}
